/**
 * The rule builder's model of a stored rule set (ADR 0039, Pos.Rules): `{}` always applies; otherwise `{"v":1,"when":node}`
 * where a node is `{"all":[...]}`, `{"any":[...]}`, `{"not":node}` or a condition `{"id","type",...parameters}`. The builder
 * edits one root group, with groups one level inside it, and each condition can be excluded ("except when").
 */

export interface RuleParam {
  name: string;
  /** reference, integer, money, quantity, time, days or date. */
  kind: string;
  required: boolean;
  reference?: string | null;
  multiple: boolean;
}

export interface RuleConditionType {
  type: string;
  /** order, where, when or items. */
  group: string;
  parameters: RuleParam[];
}

export interface ConditionNode {
  kind: 'condition';
  id: string;
  type: string;
  params: Record<string, unknown>;
  exclude: boolean;
}

export interface GroupNode {
  kind: 'group';
  match: 'all' | 'any';
  nodes: RuleNode[];
}

export type RuleNode = ConditionNode | GroupNode;

type Json = Record<string, unknown>;

const isObject = (value: unknown): value is Json =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/** The root group of a stored rule set; an empty one when it always applies. Throws on JSON the builder can't show. */
export function parseRules(text: unknown): GroupNode {
  const empty: GroupNode = { kind: 'group', match: 'all', nodes: [] };
  if (text == null || text === '') return empty;
  const parsed: unknown = typeof text === 'string' ? JSON.parse(text) : text;
  if (!isObject(parsed) || Object.keys(parsed).length === 0 || parsed.when == null) return empty;
  const root = toNode(parsed.when);
  return root.kind === 'group' ? root : { kind: 'group', match: 'all', nodes: [root] };
}

function toNode(value: unknown, exclude = false): RuleNode {
  if (!isObject(value)) throw new Error('A rule must be an object.');
  if (Array.isArray(value.all) || Array.isArray(value.any)) {
    const match = Array.isArray(value.all) ? 'all' : 'any';
    if (exclude) throw new Error('Excluding a whole group is not supported by the builder.');
    return { kind: 'group', match, nodes: (value[match] as unknown[]).map((child) => toNode(child)) };
  }
  if (value.not !== undefined) return toNode(value.not, !exclude);
  const { id, type, ...params } = value;
  if (typeof id !== 'string' || typeof type !== 'string') throw new Error('A condition needs an id and a type.');
  return { kind: 'condition', id, type, params, exclude };
}

/** The JSON to store: `{}` when there are no conditions. */
export function serializeRules(root: GroupNode): string {
  if (countConditions(root) === 0) return '{}';
  return JSON.stringify({ v: 1, when: toJson(root) });
}

function toJson(node: RuleNode): Json {
  if (node.kind === 'group') return { [node.match]: node.nodes.map(toJson) };
  const params = Object.fromEntries(
    Object.entries(node.params).filter(
      ([, v]) => v !== null && v !== undefined && !(Array.isArray(v) && v.length === 0),
    ),
  );
  const condition = { id: node.id, type: node.type, ...params };
  return node.exclude ? { not: condition } : condition;
}

export function countConditions(node: RuleNode): number {
  return node.kind === 'condition' ? 1 : node.nodes.reduce((sum, child) => sum + countConditions(child), 0);
}

/** A condition id not used yet in the rule set (`c1`, `c2`...). */
export function nextId(root: GroupNode): string {
  const used = new Set<string>();
  const walk = (node: RuleNode) => (node.kind === 'condition' ? used.add(node.id) : node.nodes.forEach(walk));
  walk(root);
  let n = 1;
  while (used.has(`c${n}`)) n += 1;
  return `c${n}`;
}

/** Sensible starting values: every day of the week, nothing chosen yet. */
export function defaultParams(type: RuleConditionType): Record<string, unknown> {
  return Object.fromEntries(type.parameters.filter((p) => p.kind === 'days').map((p) => [p.name, 127]));
}
